import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './work-orders-page.style.js';
import {
  getWorkOrdersByCompany,
  getAssetsByCompany,
  getUsersByCompany,
  deleteWorkOrder
} from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { WorkOrderStatusCat, WorkOrderPriorityCat } from '@cmms/models';
import { WorkOrder } from '../../../types/work-order.types.js';
import { createWorkOrdersTableConfig } from './work-orders-table-config.js';


@customElement('work-orders-page')
export class WorkOrdersPage extends LitElement {
  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private workOrders: WorkOrder[] = [];
  @state() private assets: any[] = [];
  @state() private users: any[] = [];

  @state() private isLoading = true;
  @state() private error: string | null = null;
  @state() private showAddModal = false;
  @state() private selectedWorkOrder: WorkOrder | null = null;
  @state() private showDeleteModal = false;
  @state() private woToDelete: WorkOrder | null = null;
  @state() private currentFilters: Record<string, any> = {};

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  // ---------------- data load & normalization ----------------
  private asArray<T = any>(resp: unknown): T[] {
    if (Array.isArray(resp)) return resp as T[];
    if (resp && typeof resp === 'object' && Array.isArray((resp as any).data)) return (resp as any).data as T[];
    return [];
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.error = null;

      const [woResp, assetsResp, usersResp] = await Promise.all([
        getWorkOrdersByCompany(this.firestore, this.companyId),
        getAssetsByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId)
      ]);

      const rawWOs = this.asArray<WorkOrder>(woResp);
      this.assets = this.asArray<any>(assetsResp);
      this.users = this.asArray<any>(usersResp);

      // Normalize WOs so UI always has what it expects
      this.workOrders = (rawWOs || []).map((wo: any) => {
        // Extract data from various possible structures (direct, model property, etc.)
        const model = wo.model || {};
        const data = { ...wo, ...model }; // Merge direct properties with model properties
        
        // Handle created_at normalization
        let created_at: number | null = null;
        const createdRaw = data.created_at || wo.created_at || model.created_at;
        if (typeof createdRaw === 'number') {
          created_at = createdRaw < 1e12 ? createdRaw * 1000 : createdRaw;
        } else if (createdRaw?.toMillis) {
          created_at = createdRaw.toMillis();
        } else if (createdRaw?.seconds) {
          created_at = createdRaw.seconds * 1000;
        }

        // Handle status normalization
        let status_cat = WorkOrderStatusCat.OPEN;
        const statusVal = data.status_cat || wo.status_cat || model.status_cat || data.status || wo.status || model.status;
        if (typeof statusVal === 'number') {
          status_cat = statusVal;
        } else if (statusVal) {
          const statusStr = String(statusVal).toLowerCase();
          if (statusStr.includes('progress')) status_cat = WorkOrderStatusCat.IN_PROGRESS;
          else if (statusStr.includes('complete')) status_cat = WorkOrderStatusCat.COMPLETED;
          else status_cat = WorkOrderStatusCat.OPEN;
        }

        // Handle priority normalization
        let priority_cat = WorkOrderPriorityCat.MEDIUM;
        const priorityVal = data.priority_cat || wo.priority_cat || model.priority_cat || data.priority || wo.priority || model.priority;
        if (typeof priorityVal === 'number') {
          priority_cat = priorityVal;
        } else if (priorityVal) {
          const priorityStr = String(priorityVal).toLowerCase();
          if (priorityStr.includes('critical')) priority_cat = WorkOrderPriorityCat.CRITICAL;
          else if (priorityStr.includes('high')) priority_cat = WorkOrderPriorityCat.HIGH;
          else if (priorityStr.includes('low')) priority_cat = WorkOrderPriorityCat.LOW;
          else priority_cat = WorkOrderPriorityCat.MEDIUM;
        }

        return {
          id: wo.id || wo.wo_id || wo.$id || model.wo_id || model.id,
          description: data.description || wo.description || model.description || '',
          status_cat,
          priority_cat,
          asset_id: data.asset_id || wo.asset_id || model.asset_id || '',
          assigned_to: data.assigned_to || wo.assigned_to || model.assigned_to || '',
          company_id: data.company_id || wo.company_id || model.company_id || this.companyId,
          created_at,
          updated_at: data.updated_at || wo.updated_at || model.updated_at || created_at,
          // Timing fields if present
          active_work_ms: data.active_work_ms ?? (wo as any).active_work_ms ?? (model as any).active_work_ms ?? 0,
          current_started_at: data.current_started_at ?? (wo as any).current_started_at ?? (model as any).current_started_at ?? null,
          completed_at: data.completed_at ?? (wo as any).completed_at ?? (model as any).completed_at ?? null
        } as WorkOrder;
      });
    } catch (e: any) {
      this.error = e?.message ?? 'Failed to load work orders';
    } finally {
      this.isLoading = false;
    }
  }



  private handleAddClick = () => { 
    this.selectedWorkOrder = null; 
    this.showAddModal = true; 
  };
  
  private handleWorkOrderClick = (wo: WorkOrder) => { 
    this.selectedWorkOrder = wo; 
  };
  
  private handleViewerClosed = () => { 
    this.selectedWorkOrder = null; 
  };
  
  private handleWorkOrderCreated = async () => { 
    this.showAddModal = false; 
    await this.loadData(); 
  };
  
  private handleModalClosed = () => { 
    this.showAddModal = false; 
  };

  private handleDeleteClick = (wo: WorkOrder) => {
    this.woToDelete = wo;
    this.showDeleteModal = true;
  };
  
  private handleDeleteCancel = () => { 
    this.showDeleteModal = false; 
    this.woToDelete = null; 
  };
  
  private handleDeleteConfirm = async () => {
    if (!this.woToDelete) return;
    try {
      await deleteWorkOrder(this.firestore, this.woToDelete.id);
      this.showDeleteModal = false;
      this.woToDelete = null;
      await this.loadData();
    } catch (error) {
      console.error('Error deleting work order:', error);
    }
  };

  private handleFilterChange = (filters: Record<string, any>) => {
    this.currentFilters = filters;
  };

  private handleSort = (column: string, direction: 'asc' | 'desc') => {
    // The data table component handles the actual sorting internally
    // This handler is called for consistency with the assets page
    console.log(`Sorting by ${column} in ${direction} direction`);
  };

  render() {
    const tableConfig = createWorkOrdersTableConfig(
      this.workOrders,
      this.assets,
      this.users,
      {
        onRowClick: this.handleWorkOrderClick,
        onDelete: this.handleDeleteClick,
        onAddWorkOrder: this.handleAddClick,
        onFilterChange: this.handleFilterChange,
        onSort: this.handleSort
      }
    );

    // Override loading and error state
    tableConfig.loading = this.isLoading;
    tableConfig.error = this.error;

    return html`
      <div class="work-orders-page">
        <data-table .config=${tableConfig}></data-table>

        <!-- Add Work Order Modal -->
        <work-order-modal
          .open=${this.showAddModal}
          @modal-closed=${this.handleModalClosed}
          @work-order-created=${this.handleWorkOrderCreated}
          .companyId=${this.companyId}
          .firestore=${this.firestore}>
        </work-order-modal>

        <!-- Work Order Viewer -->
        <work-order-viewer
          .open=${!!this.selectedWorkOrder}
          .workOrder=${this.selectedWorkOrder}
          .companyId=${this.companyId}
          .firestore=${this.firestore}
          @modal-closed=${this.handleViewerClosed}
          @work-order-updated=${this.loadData}>
        </work-order-viewer>

        <!-- Delete Confirmation -->
        <sl-dialog label="Delete Work Order" .open=${this.showDeleteModal} @sl-hide=${this.handleDeleteCancel}>
          <p>Are you sure you want to delete this work order? This action cannot be undone.</p>
          <div slot="footer">
            <sl-button size="small" @click=${this.handleDeleteCancel}>Cancel</sl-button>
            <sl-button size="small" variant="danger" @click=${this.handleDeleteConfirm}>Delete</sl-button>
          </div>
        </sl-dialog>
      </div>
    `;
  }

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'work-orders-page': WorkOrdersPage;
  }
}
